import {
  Box,
  TextField,
  Avatar,
  Typography,
  Autocomplete,
  useTheme,
  createFilterOptions,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState } from 'react';
import { Sparkles, ChevronDown } from 'lucide-mui';
import { Member, SpeakerType } from '../../../../../../types';
import { RootState } from '../../../../../../reducers';
import { getLetterAvatar } from '../../../../../../lib/commonUtils';
import { updateSpeaker } from '../../../../selectedVideoSlice';
import { FilePreviewRouteParams } from '../../../../types';

interface SpeakerSelectProps {
  speaker: SpeakerType;
  speakerId: string;
}

// Define a type that extends Member with an optional inputValue
type MemberOption =
  | Member
  | {
      inputValue: string;
      name: string;
      isAISuggestion?: boolean;
      picture?: string;
      isClearOption?: boolean;
    };

// Add type for postProcessSpeaker
type PostProcessSpeaker = Record<string, string>;

const filter = createFilterOptions<MemberOption>();

function formatSpeakerName(
  name: string,
  postProcessSpeakers?: Array<Record<string, string>>
) {
  // First check if we have a post-process alias
  if (postProcessSpeakers) {
    const speakerMatch = postProcessSpeakers.find((speaker) => speaker[name]);
    if (speakerMatch) {
      return {
        name: speakerMatch[name],
        isAISuggestion: true,
        originalName: name,
      };
    }
  }

  // Fall back to default formatting
  const match = name.match(/^SPEAKER_(\d+)$/);
  if (match) {
    const speakerNumber = parseInt(match[1], 10) + 1;
    return {
      name: `Speaker ${speakerNumber}`,
      isAISuggestion: false,
      originalName: name,
    };
  }
  return {
    name,
    isAISuggestion: false,
    originalName: name,
  };
}

const SpeakerSelect = ({ speaker, speakerId }: SpeakerSelectProps) => {
  const { teamId, fileId } = useParams<FilePreviewRouteParams>();
  const theme = useTheme();
  const { team } = useSelector((state: RootState) => state);
  const { teamSpaceMembers = [] } = team.selectedTeam || {};
  const dispatch = useDispatch();

  const [isEditing, setIsEditing] = useState(false);
  const [inputTextValue, setInputTextValue] = useState('');

  const { selectedVideo } = useSelector((state: RootState) => state);
  let postProcessSpeakers;
  try {
    postProcessSpeakers = selectedVideo.postProcess?.systemPromptResponses
      ?.SUMMARY_AND_PROMPTS?.responseText
      ? JSON.parse(
          selectedVideo.postProcess.systemPromptResponses.SUMMARY_AND_PROMPTS
            .responseText
        ).speakers
      : undefined;
  } catch (error) {
    console.error('Error parsing postProcessSpeakers:', error);
    postProcessSpeakers = undefined;
  }

  const formattedSpeakerName = formatSpeakerName(
    speaker.name,
    postProcessSpeakers
  );
  const formattedSpeakerId = formatSpeakerName(speakerId, postProcessSpeakers);
  const displayName = formattedSpeakerName.name || formattedSpeakerId.name;
  const isAISuggestion =
    (formattedSpeakerName.isAISuggestion &&
      displayName === formattedSpeakerName.name) ||
    (formattedSpeakerId.isAISuggestion &&
      displayName === formattedSpeakerId.name);

  // Get AI suggestion if it exists
  const aiSuggestion = postProcessSpeakers?.find(
    (s: PostProcessSpeaker) => s[speakerId]
  )?.[speakerId];

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: MemberOption | null
  ) => {
    if (!teamId || !fileId) return;

    if (
      newValue === null ||
      (newValue && 'inputValue' in newValue && newValue.isClearOption)
    ) {
      // When cleared (x clicked) or clear option selected, revert to default speaker name
      dispatch(
        updateSpeaker({
          teamId,
          fileId,
          speakerId,
          speaker: {
            name: speakerId, // This will revert to default SPEAKER_X format
            userId: undefined,
            picture: undefined,
          },
        })
      );
    } else if ('inputValue' in newValue) {
      dispatch(
        updateSpeaker({
          teamId,
          fileId,
          speakerId,
          speaker: {
            name: newValue.inputValue,
            userId: undefined,
            picture: undefined,
          },
        })
      );
    } else if ('name' in newValue) {
      const { name, picture, _id: userId } = newValue;
      dispatch(
        updateSpeaker({
          teamId,
          fileId,
          speakerId,
          speaker: {
            userId,
            name,
            picture,
          },
        })
      );
    }
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false); // Close editing mode if user clicks outside
  };

  const filterOptions = (options: MemberOption[], params: any) => {
    const filtered = filter(options, params);

    const { inputValue: input } = params;
    // Suggest the creation of a new value
    const isExisting = options.some(
      (option) => 'name' in option && input === option.name
    );
    if (input !== '' && !isExisting) {
      filtered.push({
        inputValue: input,
        name: `Add "${input}"`,
      });
    }

    // Add AI suggestion to the top of the list if it exists and isn't already selected
    if (aiSuggestion && speaker.name !== aiSuggestion) {
      filtered.unshift({
        inputValue: aiSuggestion,
        name: aiSuggestion,
        isAISuggestion: true,
      });
    }

    // Add current speaker name to options if it's not a team member AND there's no AI suggestion
    if (speaker.name && !speaker.userId && !aiSuggestion) {
      const currentSpeakerOption = {
        inputValue: speaker.name,
        name: speaker.name,
        isAISuggestion,
      };

      // Check if it's not already in the list
      const exists = filtered.some(
        (option) =>
          ('inputValue' in option && option.inputValue === speaker.name) ||
          ('name' in option && option.name === speaker.name)
      );

      if (!exists) {
        filtered.push(currentSpeakerOption);
      }
    }

    return filtered;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        columnGap: theme.spacing(1),
        cursor: 'pointer',
      }}
    >
      <Avatar alt={displayName} src={speaker.picture || undefined}>
        {getLetterAvatar(displayName)}
      </Avatar>
      {isEditing ? (
        <Autocomplete
          value={
            teamSpaceMembers.find((member) => member._id === speaker.userId) ||
            null
          }
          onChange={handleChange}
          options={teamSpaceMembers}
          open={isEditing}
          onOpen={() => {}}
          onBlur={handleBlur}
          autoHighlight
          clearOnBlur={false}
          clearOnEscape={false}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }

            if ('inputValue' in option) {
              return option.inputValue;
            }

            return option.name;
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: theme.spacing(1),
                minHeight: '40px',
                padding: theme.spacing(1, 2),
              }}
            >
              <Avatar src={'inputValue' in option ? undefined : option.picture}>
                {getLetterAvatar(
                  'inputValue' in option ? option.inputValue : option.name
                )}
              </Avatar>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {'inputValue' in option && option.isAISuggestion && (
                  <Sparkles
                    sx={{
                      fontSize: '1rem',
                      color: 'secondary.contrastText',
                    }}
                  />
                )}
                <Typography>
                  {'inputValue' in option ? option.inputValue : option.name}
                </Typography>
              </Box>
            </Box>
          )}
          freeSolo
          inputValue={inputTextValue}
          onInputChange={(event, newInputValue, reason) => {
            setInputTextValue(newInputValue);

            if (reason === 'clear') {
              return;
            }

            if (!newInputValue && aiSuggestion) {
              handleChange(event as React.SyntheticEvent, {
                inputValue: aiSuggestion,
                name: aiSuggestion,
                isAISuggestion: true,
              });
            }
          }}
          filterOptions={filterOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              fullWidth
              autoFocus
              placeholder="Select or type a name"
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Select speaker',
                style: {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: theme.spacing(1, 0.5),
                },
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => {
            if ('inputValue' in option || 'inputValue' in value) return false;
            return option._id === value._id;
          }}
          sx={{
            width: '300px',
            '& .MuiInputBase-root': {
              height: '40px',
              padding: theme.spacing(0, 1),
              backgroundColor: theme.palette.action.hover,
              '& fieldset': {
                border: '1px solid',
                borderColor: theme.palette.action.selected,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.text.secondary,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.secondary.main,
              },
            },
            '& .MuiAutocomplete-endAdornment': {
              right: theme.spacing(1),
            },
            '& .MuiAutocomplete-clearIndicator': {
              color: theme.palette.text.secondary,
            },
            '& .MuiAutocomplete-popupIndicator': {
              color: theme.palette.text.secondary,
            },
            '& .MuiAutocomplete-popper .MuiPaper-root': {
              marginTop: theme.spacing(1),
            },
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();

              // If there's a direct input value, use that
              if (inputTextValue) {
                const syntheticEvent = (new Event(
                  'synthetic'
                ) as unknown) as React.SyntheticEvent;

                // Check if the input matches an existing team member
                const matchingTeamMember = teamSpaceMembers.find(
                  (member) =>
                    member.name.toLowerCase() === inputTextValue.toLowerCase()
                );

                if (matchingTeamMember) {
                  // If it matches a team member, use that
                  handleChange(syntheticEvent, matchingTeamMember);
                } else {
                  // Otherwise create a new custom speaker
                  handleChange(syntheticEvent, {
                    inputValue: inputTextValue,
                    name: inputTextValue,
                  });
                }

                setIsEditing(false);
              }
            }
          }}
        />
      ) : (
        <Box
          onClick={handleEditClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.secondary.main}`,
              outlineOffset: '2px',
            },
            borderRadius: '4px',
            padding: '8px 12px',
            height: '40px',
            transition: theme.transitions.create([
              'background-color',
              'box-shadow',
            ]),
            role: 'button',
            tabIndex: 0,
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleEditClick();
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: isAISuggestion
                ? 'secondary.contrastText'
                : 'text.secondary',
            }}
          >
            {isAISuggestion && (
              <Sparkles
                sx={{
                  fontSize: '1rem',
                  color: 'secondary.contrastText',
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                color: isAISuggestion
                  ? 'secondary.contrastText'
                  : 'text.secondary',
              }}
            >
              {displayName}
            </Typography>
            <ChevronDown
              sx={{
                fontSize: '1.25rem',
                color: 'text.secondary',
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SpeakerSelect;
